import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Link,
  Typography
} from '@mui/material';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import MiscellaneousServicesOutlined from '@mui/icons-material/MiscellaneousServicesOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import { useGetExecutiveEntityQuickViewQuery } from '../../../features/apiSlice/entityApi';
import '../QuickView/styles.scss';
import './styles.scss';

export const formatEntityAddress = (address) => {
  if (!address) return 'Address not available';
  return (
    [address.unit, address.street, address.city, address.region, address.postal]
      .filter(Boolean)
      .join(', ') || 'Address not available'
  );
};

const getCount = (stats, type) =>
  Number(stats?.find((stat) => stat.type === type)?.count) || 0;

const Metric = ({ icon, label, value }) => (
  <Box className="executive-entity-quick-view__metric">
    {icon}
    <Typography className="executive-entity-quick-view__metric-value">
      {value}
    </Typography>
    <Typography className="executive-entity-quick-view__metric-label">
      {label}
    </Typography>
  </Box>
);

Metric.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired
};

const ExecutiveEntityQuickView = ({ entity, onClose }) => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetExecutiveEntityQuickViewQuery(
    { id: entity?.id },
    { skip: !entity?.id }
  );

  const displayEntity = data || entity || {};
  const addressRecord = data?.addresses?.[0];
  const address = formatEntityAddress(addressRecord);
  const coordinates =
    addressRecord?.lat != null && addressRecord?.long != null
      ? `${addressRecord.lat},${addressRecord.long}`
      : null;
  const locations = getCount(data?.stats, 'assets');
  const accounts = getCount(data?.stats, 'contracts');
  const services = Array.isArray(data?.service_ids)
    ? data.service_ids.length
    : 0;

  return (
    <Container disableGutters className="quick-view">
      <Card className="quick-view-root executive-entity-quick-view">
        <IconButton
          aria-label="close entity quick view"
          className="quick-view-close"
          color="error"
          onClick={onClose}
        >
          <Close fontSize="small" />
        </IconButton>

        <Box className="executive-entity-quick-view__header">
          <BusinessOutlined className="executive-entity-quick-view__entity-icon" />
          <Box>
            <Typography className="executive-entity-quick-view__eyebrow">
              Strategic entity
            </Typography>
            <Typography
              variant="h5"
              className="executive-entity-quick-view__title"
            >
              {displayEntity.name || 'Entity'}
            </Typography>
          </Box>
        </Box>

        {isLoading ? (
          <Box className="executive-entity-quick-view__status">
            <CircularProgress size={28} />
            <Typography>Loading entity details...</Typography>
          </Box>
        ) : isError ? (
          <Box className="executive-entity-quick-view__status" role="alert">
            <Typography>Entity details could not be loaded.</Typography>
          </Box>
        ) : (
          <>
            <Box className="executive-entity-quick-view__contact">
              {coordinates ? (
                <Link
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coordinates)}`}
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                  className="executive-entity-quick-view__contact-row"
                >
                  <PlaceOutlined />
                  <span>{address}</span>
                </Link>
              ) : (
                <Box className="executive-entity-quick-view__contact-row">
                  <PlaceOutlined />
                  <span>{address}</span>
                </Box>
              )}
              {displayEntity.phone && (
                <Link
                  href={`tel:${displayEntity.phone}`}
                  underline="hover"
                  className="executive-entity-quick-view__contact-row"
                >
                  <PhoneOutlined />
                  <span>{displayEntity.phone}</span>
                </Link>
              )}
            </Box>

            <Divider />

            <Box className="executive-entity-quick-view__metrics">
              <Metric
                icon={<PlaceOutlined />}
                label="Locations"
                value={locations}
              />
              <Metric
                icon={<MiscellaneousServicesOutlined />}
                label="Services"
                value={services}
              />
              <Metric
                icon={<AccountBalanceOutlined />}
                label="Accounts"
                value={accounts}
              />
            </Box>
          </>
        )}

        <Box className="quick-view-action-wrapper">
          <Button
            variant="outlined"
            endIcon={<ChevronRight />}
            fullWidth
            className="quick-view-action"
            disabled={!entity?.id}
            onClick={() => navigate(`/executive/entity/${entity.id}`)}
          >
            View entity details
          </Button>
        </Box>
      </Card>
    </Container>
  );
};

ExecutiveEntityQuickView.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired
};

export default memo(ExecutiveEntityQuickView);
